
import React, { useState, useMemo, useCallback } from "react";
import { useFormikContext } from "formik";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import Button from "../../../components/ui/Button";
import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { PitchFormValues } from "../Schema/pitchValidationSchema";
import { SelectedDSPs } from "../../types/form";

interface DspMarketingNote {
  dspName: string;
  note: string;
  id: string;
}

interface DspMarketingNotesProps {
  selectedDSPs: SelectedDSPs;
}

interface DeleteConfirmationState {
  show: boolean;
  noteId: string;
  dspName: string;
}

const DSP_MAPPING: Record<keyof SelectedDSPs, string> = {
  spotify: "Spotify",
  amazon: "Amazon Music",
  deezer: "Deezer",
  audiomack: "Audiomack",
  apple: "Apple Music",
  TIDAL: "TIDAL",
  youtube: "YouTube",
  soundcloud: "SoundCloud",
  email: "Email",
  siriusxm: "SiriusXM",
  vevo: "Vevo"
} as const;

const generateNoteId = (): string =>
  `dsp-note-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const MAX_CHARACTERS = 200;

const DspMarketingNotes: React.FC<DspMarketingNotesProps> = ({ selectedDSPs }) => {
  const { values, setFieldValue } = useFormikContext<PitchFormValues>();
  
  // State management
  const [showDspDropdown, setShowDspDropdown] = useState(false);
  const [selectedDsp, setSelectedDsp] = useState("");
  const [dspNote, setDspNote] = useState("");
  const [editingDspNoteId, setEditingDspNoteId] = useState<string | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<DeleteConfirmationState>({
    show: false,
    noteId: "",
    dspName: ""
  });

  // Memoized computed values
  const currentNotes = useMemo(() => 
    (values.dspMarketingNotes || []) as DspMarketingNote[], 
    [values.dspMarketingNotes]
  );

  const availableDsps = useMemo(() => {
    return Object.entries(selectedDSPs)
      .filter(([key, isSelected]) => isSelected && key !== 'email')
      .map(([key]) => DSP_MAPPING[key as keyof SelectedDSPs])
      .filter(Boolean);
  }, [selectedDSPs]);

  const availableDspsForDropdown = useMemo(() => {
    const dspNamesWithNotes = new Set(currentNotes.map(note => note.dspName));

    if (editingDspNoteId) {
      const editingNote = currentNotes.find(note => note.id === editingDspNoteId);
      if (editingNote) {
        dspNamesWithNotes.delete(editingNote.dspName);
      }
    }

    return availableDsps.filter(dsp => !dspNamesWithNotes.has(dsp));
  }, [availableDsps, currentNotes, editingDspNoteId]);

  const canAddNote = useMemo(() =>
    availableDsps.length > 0 && availableDspsForDropdown.length > 0,
    [availableDsps.length, availableDspsForDropdown.length]
  );

  // Optimized handlers with useCallback
  const resetForm = useCallback(() => {
    setShowDspDropdown(false);
    setSelectedDsp("");
    setDspNote("");
    setEditingDspNoteId(null);
  }, []);

  // Close dropdown if selected DSP is deselected from platform selection
  React.useEffect(() => {
    if (showDspDropdown && selectedDsp && !availableDsps.includes(selectedDsp)) {
      // The currently selected DSP was deselected from platform selection
      resetForm();
    }
  }, [showDspDropdown, selectedDsp, availableDsps, resetForm]);

  // Close dropdown if no DSPs are available anymore
  React.useEffect(() => {
    if (showDspDropdown && availableDspsForDropdown.length === 0 && !editingDspNoteId) {
      // No DSPs available to add, close the dropdown
      resetForm();
    }
  }, [showDspDropdown, availableDspsForDropdown.length, editingDspNoteId, resetForm]);

  const handleAddDspNote = useCallback(() => {
    setShowDspDropdown(true);
    setSelectedDsp("");
    setDspNote("");
    setEditingDspNoteId(null);
  }, []);

  const handleDspSelect = useCallback((dsp: string) => {
    setSelectedDsp(dsp);
  }, []);

  const handleSaveDspNote = useCallback(() => {
    if (!selectedDsp || !dspNote.trim()) return;

    if (editingDspNoteId) {
      const updatedNotes = currentNotes.map((note) =>
        note.id === editingDspNoteId ? { ...note, dspName: selectedDsp, note: dspNote } : note
      );
      setFieldValue("dspMarketingNotes", updatedNotes);
    } else {
      const newNote: DspMarketingNote = {
        dspName: selectedDsp,
        note: dspNote,
        id: generateNoteId()
      };
      setFieldValue("dspMarketingNotes", [...currentNotes, newNote]);
    }

    resetForm();
  }, [selectedDsp, dspNote, editingDspNoteId, currentNotes, setFieldValue, resetForm]);

  const handleEditDspNote = useCallback((noteId: string) => {
    const noteToEdit = currentNotes.find((note) => note.id === noteId);

    if (noteToEdit) {
      setSelectedDsp(noteToEdit.dspName);
      setDspNote(noteToEdit.note);
      setEditingDspNoteId(noteId);
      setShowDspDropdown(true);
    }
  }, [currentNotes]);

  const handleDeleteClick = useCallback((noteId: string, dspName: string) => {
    setDeleteConfirmation({ show: true, noteId, dspName });
  }, []);

  const handleConfirmDelete = useCallback(() => {
    const updatedNotes = currentNotes.filter((note) => note.id !== deleteConfirmation.noteId);
    setFieldValue("dspMarketingNotes", updatedNotes);
    setDeleteConfirmation({ show: false, noteId: "", dspName: "" });
  }, [currentNotes, deleteConfirmation.noteId, setFieldValue]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmation({ show: false, noteId: "", dspName: "" });
  }, []);

  const handleNoteChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    if (newValue.length <= MAX_CHARACTERS) {
      setDspNote(newValue);
    }
  }, []);

  return (
    <div className="mt-6 space-y-4">
      {/* Delete Confirmation Modal */}
      {deleteConfirmation.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-center mb-4">
              <div className="w-10 h-10 bg-danger-100 rounded-full flex items-center justify-center">
                <TrashIcon className="w-6 h-6 text-danger-600" />
              </div>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-medium text-neutral-900 mb-2">
                Delete Marketing Note
              </h3>
              <p className="text-sm text-neutral-500 mb-6">
                Are you sure you want to delete the marketing note for{" "}
                <span className="font-medium text-neutral-900">
                  {deleteConfirmation.dspName}
                </span>
                ? This action cannot be undone.
              </p>
              <div className="flex gap-3 justify-center">
                <Button
                  type="button"
                  onClick={handleCancelDelete}
                  variant="neutral"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleConfirmDelete}
                  variant="danger"
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header with Add Button */}
      <div className="flex items-center justify-between">
        <label className="block mb-2 text-sm font-medium text-neutral-700">
          DSP-Specific Marketing Info
        </label>
        <Button
          type="button"
          onClick={handleAddDspNote}
          disabled={!canAddNote}
          variant="soft"
          size="sm"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add DSP-Specific Marketing Info
        </Button>
      </div>

      {/* DSP Dropdown and Input Section */}
      {showDspDropdown && (
        <div className="p-4 space-y-4 bg-neutral-50 border border-neutral-200 rounded-lg">
          <div className="space-y-2">
            <label className="block mb-2 text-sm font-medium text-neutral-700">
              Select DSP
            </label>
            <Select value={selectedDsp} onValueChange={handleDspSelect}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a DSP" />
              </SelectTrigger>
              <SelectContent>
                {availableDspsForDropdown.length > 0 ? (
                  availableDspsForDropdown.map((dsp) => (
                    <SelectItem key={dsp} value={dsp}>
                      {dsp}
                    </SelectItem>
                  ))
                ) : (
                  <div className="px-2 py-3 text-sm text-neutral-500 text-center">
                    All selected DSPs already have notes
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>

          {selectedDsp && (
            <>
              <div className="space-y-2">
                <label className="block mb-2 text-sm font-medium text-neutral-700">
                  {selectedDsp} Marketing Note
                </label>
                <div className="relative">
                  <textarea
                    placeholder={`Add marketing information specific to ${selectedDsp}.`}
                    value={dspNote}
                    onChange={handleNoteChange}
                    rows={4}
                    maxLength={MAX_CHARACTERS}
                    className="w-full px-3 py-2 text-sm text-black bg-white border rounded-md outline-none transition-colors border-neutral-300 placeholder-neutral-400 focus:border-primary-500"
                  />
                  <div className={`absolute bottom-2 right-3 text-xs ${dspNote.length >= MAX_CHARACTERS ? 'text-danger-600 font-medium' : 'text-neutral-500'}`}>
                    {dspNote.length}/{MAX_CHARACTERS}
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={handleSaveDspNote}
                  disabled={!dspNote.trim()}
                >
                  {editingDspNoteId ? "Update Note" : "Save Note"}
                </Button>
                <Button
                  type="button"
                  onClick={resetForm}
                  variant="neutral"
                >
                  Cancel
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Display Added Notes */}
      {currentNotes.length > 0 && (
        <div className="space-y-3 mt-4">
          {currentNotes.map((note) => (
            <div
              key={note.id}
              className="p-4 bg-white border border-neutral-200 rounded-lg shadow-sm"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-neutral-900 mb-2">
                    {note.dspName}
                  </h4>
                  <p className="text-sm text-neutral-700 whitespace-pre-wrap">
                    {note.note}
                  </p>
                </div>
                <div className="flex gap-2 ml-4">
                  <button
                    type="button"
                    onClick={() => handleEditDspNote(note.id)}
                    className="p-1 text-primary-700 hover:text-primary-800 focus:outline-none"
                    title="Edit note"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteClick(note.id, note.dspName)}
                    className="p-1 text-danger-600 hover:text-danger-800 focus:outline-none"
                    title="Delete note"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DspMarketingNotes;